"use client"

import { useEffect, useState } from "react"
import { useLocale, useTranslations } from "next-intl"
import { ProfileControl } from "@owlcat/identity-react"

const stripSlash = (u: string) => u.replace(/\/+$/, "")

/** Parent (apex) domain — used to synthesize the accounts + apex fallbacks. */
const PARENT_DOMAIN =
  process.env.NEXT_PUBLIC_OWLCAT_PARENT_DOMAIN || "owlcat.fartusov.com"

/** Central accounts SSO authority (parent-domain owlcat-id cookie). */
const ACCOUNTS_URL = stripSlash(
  process.env.NEXT_PUBLIC_ACCOUNTS_URL || `https://accounts.${PARENT_DOMAIN}`
)

/**
 * FleetProfile — the shared fleet identity control in the shop header.
 *
 * CLIENT-ONLY: the shared `@owlcat/identity-react` ProfileControl does a
 * credentialed cross-subdomain `GET /me` (the parent-domain `owlcat-id` cookie
 * rides along), so a session started in ANY sibling app — landing, games,
 * portal — renders here as "logged in as X", exactly like every other fleet
 * header. No session → a "Sign in" deep-link back to accounts.
 *
 * This is fleet identity DISPLAY + sign-in parity ONLY. It is INDEPENDENT of
 * the shop's own next-auth cart/checkout session (see AccountLink); the two
 * coexist. `returnTo` is the live shop URL so accounts bounces the user straight
 * back after login/logout.
 */
export default function FleetProfile() {
  const t = useTranslations("common")
  const locale = useLocale()
  const [returnTo, setReturnTo] = useState<string | undefined>(undefined)

  // `window` is unavailable during SSR; defer the control (and its live
  // returnTo) to the client so the login href can't mismatch on hydration.
  useEffect(() => {
    setReturnTo(window.location.href)
  }, [])

  if (!returnTo) return null

  return (
    <ProfileControl
      locale={locale}
      accountsUrl={ACCOUNTS_URL}
      appId="shop"
      selfPath={process.env.NEXT_PUBLIC_ACCOUNTS_SELF_PATH || "/me"}
      profileUrl={`${ACCOUNTS_URL}/profile?lang=${encodeURIComponent(locale)}`}
      appsUrl={`https://${PARENT_DOMAIN}/${locale}/`}
      returnTo={returnTo}
      labels={{
        signIn: t("nav.login"),
        signOut: t("nav.logout"),
        account: t("nav.account"),
        profile: t("nav.myAccount"),
        yourApps: t("nav.yourApps"),
      }}
    />
  )
}
